const AUTH_TOKEN_KEY = 'SFD-DEVOPS-TOKEN';
const AUTH_USER_KEY = 'auth_current_user';

type AnyRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === 'object' && value !== null;

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const readStorage = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
};

const removeStorage = (key: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
};

const removeCookie = (key: string) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${key}=; Max-Age=0; path=/`;
};

const parseCode = (code: unknown): number | undefined => {
  if (typeof code === 'number') {
    return code;
  }
  if (typeof code === 'string' && code.trim()) {
    const parsed = Number(code);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const findMessage = (source?: AnyRecord): string | undefined => {
  if (!source) {
    return undefined;
  }
  return pickNonEmptyString(
    source.msg,
    source.message,
    source.errorMessage,
    source.errorMsg,
  );
};

const findToken = (source?: AnyRecord): string | undefined => {
  if (!source) {
    return undefined;
  }
  return pickNonEmptyString(source.tokenValue);
};

const normalizeUser = (
  candidate: AnyRecord | undefined,
  loginName?: string,
): API.CurrentUser => {
  const name = pickNonEmptyString(
    candidate?.name,
    candidate?.nickName,
    candidate?.nickname,
    candidate?.username,
    candidate?.loginName,
    loginName,
  );

  return {
    ...candidate,
    name,
  };
};

const nestedRecord = (payload: AnyRecord, key: string): AnyRecord | undefined => {
  const value = payload[key];
  return isRecord(value) ? value : undefined;
};

export const clearAuthCookie = () => {
  removeCookie(AUTH_TOKEN_KEY);
};

export const getAuthToken = (): string | undefined => {
  return readStorage(AUTH_TOKEN_KEY) || undefined;
};

export const setAuthToken = (token: string) => {
  clearAuthCookie();
  writeStorage(AUTH_TOKEN_KEY, token.trim());
};

export const clearAuthStorage = () => {
  removeStorage(AUTH_TOKEN_KEY);
  clearAuthCookie();
  removeStorage(AUTH_USER_KEY);
};

export const getCurrentUser = (): API.CurrentUser | undefined => {
  const raw = readStorage(AUTH_USER_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw);
    return isRecord(parsed) ? (parsed as API.CurrentUser) : undefined;
  } catch {
    return undefined;
  }
};

export const setCurrentUser = (user: API.CurrentUser) => {
  writeStorage(AUTH_USER_KEY, JSON.stringify(user));
};

export const extractLoginMessage = (payload: unknown): string => {
  if (!isRecord(payload)) {
    return '';
  }
  const data = nestedRecord(payload, 'data');
  return findMessage(payload) || findMessage(data) || '';
};

export const isLoginSuccess = (payload: unknown): boolean => {
  if (!isRecord(payload)) {
    return true;
  }

  const data = nestedRecord(payload, 'data');
  const status = pickNonEmptyString(payload.status, data?.status)?.toLowerCase();
  if (status) {
    if (['ok', 'success', 'succeeded', '200'].includes(status)) {
      return true;
    }
    if (['error', 'fail', 'failed'].includes(status)) {
      return false;
    }
  }

  const success = payload.success;
  if (typeof success === 'boolean') {
    return success;
  }

  const codeCandidates = [
    payload.code,
    payload.statusCode,
    payload.errorCode,
    data?.code,
    data?.statusCode,
  ];
  for (const candidate of codeCandidates) {
    const parsedCode = parseCode(candidate);
    if (parsedCode === undefined) {
      continue;
    }
    if (parsedCode === 0 || parsedCode === 200) {
      return true;
    }
    if (parsedCode >= 400) {
      return false;
    }
  }

  return true;
};

export const saveLoginSession = (
  payload: unknown,
  loginName?: string,
): API.CurrentUser => {
  const root = isRecord(payload) ? payload : {};
  const rawData = root.data;
  const data = nestedRecord(root, 'data');

  const token =
    findToken(root) ||
    (typeof rawData === 'string' ? rawData : undefined) ||
    findToken(data) ||
    findToken(nestedRecord(root, 'result')) ||
    findToken(nestedRecord(data || {}, 'result'));

  if (!token?.trim()) {
    throw new Error('登录接口未返回 tokenValue');
  }
  setAuthToken(token);

  const userCandidate =
    nestedRecord(root, 'user') ||
    nestedRecord(root, 'currentUser') ||
    nestedRecord(data || {}, 'user') ||
    nestedRecord(data || {}, 'currentUser') ||
    (isRecord(data) ? data : undefined);

  const user = normalizeUser(userCandidate, loginName);
  setCurrentUser(user);
  return user;
};
